import {
  phoneRegExp,
  urlRegExp,
  zipCodeRegExp,
  postalCodeRegExp,
  Country,
  States,
  Provinces,
  RegionType,
  RegionCode,
  normalizeText,
} from './common/helpers';
import { SelectInput, TextInput } from './common';
import { useMutation } from '@apollo/react-hooks';
import { Account } from '../App';
import { Formik, Form, FormikHelpers } from 'formik';
import gql from 'graphql-tag';
import { useState } from 'react';
import { Button, Form as BForm, Modal, Col } from 'react-bootstrap';
import * as Yup from 'yup';

import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/modals.css';
import { STATUS_CODES } from 'http';

const registerMut = gql`
  mutation createAccount($user: UserInput!, $firm: OrganizationInput!) {
    createAccount(user: $user, firm: $firm)
  }
`;
interface FirmInfo {
  firmName: string;
  address1: string | null;
  city: string | null;
  postal: string | null;
  state: States | Provinces;
  country: Country;
  phone: string;
  websiteUrl: string | null;
}

interface props {
  account: Account | null;
  showModal: boolean;
  closeModal: () => void;
}

export default function FirmInformation({
  account,
  showModal = false,
  closeModal,
}: props) {
  const [error, setError] = useState<string>('');
  const [regionType, setRegionType] = useState<RegionType>(RegionType.State);
  const [regionCode, setRegionCode] = useState<RegionCode>(
    RegionCode[Country.USA]
  );
  const [regions, setRegions] = useState<States[] | Provinces[]>(
    Object.values(States)
  );

  const [registerAccount] = useMutation(registerMut);

  function handleCloseModal() {
    setError('');
    closeModal();
  }

  function handleCountryChange(country: Country) {
    setRegionType(Country.Canada ? RegionType.Province : RegionType.State);
    setRegionCode(RegionCode[country]);
    setRegions(Object.values(country === Country.Canada ? Provinces : States));
  }

  return (
    <>
      <Modal
        backdrop="static"
        keyboard={false}
        show={showModal}
        onHide={handleCloseModal}
        contentClassName="ec-modal-content"
      >
        <Modal.Header className="ec-modal-header" closeButton>
          <Modal.Title className="ec-modal-title">Firm Information</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            firmName: '',
            address1: null,
            city: null,
            postal: null,
            state: States.null,
            country: Country.USA,
            phone: '',
            websiteUrl: null,
          }}
          validationSchema={Yup.object({
            firmName: Yup.string()
              .trim()
              .max(200)
              .required('Non-law firms can use an email address'),
            state: Yup.string().required('Required'),
            country: Yup.string().required('Required'),
            address1: Yup.string().trim().max(300).nullable(),
            city: Yup.string().trim().max(100).nullable(),
            postal: Yup.string()
              .trim()
              .max(20)
              .matches(
                regionCode === RegionCode.Canada
                  ? postalCodeRegExp
                  : zipCodeRegExp,
                regionCode === RegionCode.Canada
                  ? 'Postal code is not valid'
                  : 'Zip code is not valid'
              )
              .nullable(),
            phone: Yup.string()
              .trim()
              .max(30)
              .matches(phoneRegExp, 'Phone number is not valid')
              .required('Required'),
            websiteUrl: Yup.string()
              .trim()
              .max(2048)
              .matches(urlRegExp, 'URL is not valid')
              .nullable(),
          })}
          onSubmit={async (
            values: FirmInfo,
            { setSubmitting }: any //FormikHelpers<FirmInfo>
          ) => {
            debugger;
            registerAccount({
              variables: {
                user: account,
                firm: {
                  firmName: normalizeText(values.firmName) || '',
                  address1: normalizeText(values.address1),
                  city: normalizeText(values.city),
                  postal: normalizeText(values.postal),
                  state: values.state,
                  country: values.country,
                  phone: normalizeText(values.phone) || '',
                  websiteUrl: normalizeText(values.websiteUrl),
                },
              },
            })
              .then((data) => {
                localStorage.setItem('token', data.data.createAccount);
                setSubmitting(false);
                handleCloseModal();
                // history.push('/main');
                // window.location.assign('/#/main');
                window.location.assign('/main');
              })
              .catch((res) => {
                // TODO: cleanup error messages with custom ERRORs sent from the backend
                if (res?.networkError?.result?.errors?.length > 0) {
                  let errors = '';
                  // TODO: Migration to apollo-server instead of express-graphql solves the problem
                  // https://www.javaer101.com/en/article/33753010.html
                  // https://stackoverflow.com/questions/59465864/handling-errors-with-react-apollo-usemutation-hook
                  res.networkError.result.errors.forEach((error: any) => {
                    errors += error.message + ' ';
                  });
                  setError(errors);
                } else {
                  setError(
                    'Sorry, please try again. Contact Evichat if the error persists.'
                  );
                }
              });
          }}
        >
          {({ errors, handleChange, isSubmitting, setFieldValue }) => (
            <Form>
              <Modal.Body className="ec-modal-body">
                <BForm.Group controlId="formBasicFirmName">
                  <TextInput
                    label="Firm name"
                    name="firmName"
                    type="text"
                    autoComplete="organization"
                  />
                </BForm.Group>
                <BForm.Group controlId="formBasicFirmAddress">
                  <TextInput
                    label="Firm address"
                    name="address1"
                    type="text"
                    autoComplete="address-line1"
                  />
                </BForm.Group>
                <BForm.Group controlId="formBasicCity">
                  <TextInput
                    label="City"
                    name="city"
                    type="text"
                    autoComplete="address-level2"
                  />
                </BForm.Group>
                <BForm.Group controlId="formBasicZipCode">
                  <TextInput
                    label={regionCode}
                    name="postal"
                    type="text"
                    autoComplete="postal-code"
                  />
                </BForm.Group>
                <BForm.Row>
                  <BForm.Group
                    as={Col}
                    controlId="formBasicRegion"
                    className="mb-0"
                  >
                    <SelectInput
                      label={regionType}
                      name="state"
                      autoComplete="address-level1"
                      onChange={(e: React.ChangeEvent<any>) => handleChange(e)}
                      options={regions}
                    />
                  </BForm.Group>
                  <BForm.Group
                    as={Col}
                    controlId="formBasicCountry"
                    className="mb-0"
                  >
                    <SelectInput
                      label="Country"
                      name="country"
                      autoComplete="country-name"
                      onChange={(e: React.ChangeEvent<any>) => {
                        const country: Country = e.target.value;
                        handleCountryChange(country);
                        handleChange(e);
                        setFieldValue('country', country);
                        setFieldValue(
                          'state',
                          Object.values(
                            country === Country.Canada ? Provinces : States
                          )[0]
                        );
                      }}
                      options={Object.values(Country)}
                    />
                  </BForm.Group>
                </BForm.Row>
                <BForm.Group controlId="formBasicOfficeNumber">
                  <TextInput
                    label="Office number"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                  />
                </BForm.Group>
                <BForm.Group controlId="formBasicWebsiteUrl">
                  <TextInput
                    label="Website URL"
                    name="websiteUrl"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                  />
                </BForm.Group>

                <BForm.Text className="ec-modal-error-msg ec-modal-login-error-msg">
                  <p>{error}</p>
                </BForm.Text>
              </Modal.Body>
              <Modal.Footer className="ec-modal-footer ec-modal-footer-firm-info">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className="ec-modal-create-account-submit"
                >
                  Create Account
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
}
